import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useState } from 'react';
import axios from 'axios';
import * as Sentry from '@sentry/react';

/**
 * Encodes a filename for use in the Content-Disposition header.
 * Returns an ASCII-safe fallback for `filename` and a UTF-8 encoded `filename*` per RFC 5987.
 *
 * @example
 * encodeContentDisposition("Rechnung_März.pdf")
 * // => 'attachment; filename="Rechnung_Mrz.pdf"; filename*=UTF-8\'\'Rechnung_M%C3%A4rz.pdf'
 */
function encodeContentDisposition(filename: string): string {
  // ASCII-safe fallback: strip non-ASCII characters, then escape \ and "
  const asciiFallback = filename
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"');
  // RFC 5987 UTF-8 encoded filename
  const utf8Encoded = encodeURIComponent(filename).replace(
    /['()]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`
  );
  return `attachment; filename="${asciiFallback}"; filename*=UTF-8''${utf8Encoded}`;
}

export interface UploadState {
  progress: number;
  error?: Error;
  finished: boolean;
  receiptImageId?: string;
}

export type UploadFile = (
  image: File,
  fileType?: string,
  useFetch?: boolean,
  /** Overwrite the object stored under this id instead of minting a new one. */
  imageId?: string
) => Promise<string | undefined>;

interface UseUploadReceiptImageResult {
  upload: UploadFile;
  uploadState: UploadState | undefined;
}

interface UseUploadReceiptImageProps {
  uuid: () => string;
  computeMd5Hash: (file: File) => Promise<string>;
  getUploadUrl: (
    receiptId: string,
    fileType: string,
    md5Hash: string
  ) => Promise<string | undefined>;
}

// Windows file explorer offers pjp/pjpeg/jfif when the OS-known type is image/jpeg.
const MIME_BY_EXTENSION: Record<string, string> = {
  pjp: 'image/jpeg',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  pjpeg: 'image/jpeg',
  jfif: 'image/jpeg',
  png: 'image/png',
  pdf: 'application/pdf',
};

const SUPPORTED_MIME_TYPES: ReadonlySet<string> = new Set(
  Object.values(MIME_BY_EXTENSION)
);

function determineFileTypeFromFileName(fileName: string): string | undefined {
  const parts = fileName.split('.');
  if (parts.length < 2) {
    return undefined;
  }
  return MIME_BY_EXTENSION[parts[parts.length - 1].toLowerCase()];
}

/**
 * Resolves a file's MIME type, but only when it matches a type the receipt
 * pipeline supports (JPEG, PNG, PDF). Prefers the browser-provided
 * `File.type` and falls back to the filename extension. Returns `undefined`
 * for anything else so callers can show a clear error before starting the
 * upload — instead of silently letting an unsupported file (e.g. HEIC) reach
 * S3 where the preview pipeline can't render it.
 *
 * @example
 *   resolveFileType(new File([], "receipt.pdf")) // => "application/pdf"
 *   resolveFileType(new File([], "scan.heic", { type: "image/heic" })) // => undefined
 *   resolveFileType(new File([], "mystery")) // => undefined
 */
export function resolveFileType(file: File): string | undefined {
  if (file.type && SUPPORTED_MIME_TYPES.has(file.type)) {
    return file.type;
  }
  return determineFileTypeFromFileName(file.name);
}

export const useUploadFileBase = ({
  uuid,
  getUploadUrl,
  setUploadState,
  computeMd5Hash,
}: UseUploadReceiptImageProps & {
  setUploadState?: Dispatch<SetStateAction<UploadState | undefined>>;
}): UseUploadReceiptImageResult['upload'] => {
  return useCallback(
    async (
      file: File,
      fileType?: string,
      useFetch?: boolean,
      imageId?: string
    ): Promise<string | undefined> => {
      try {
        fileType = fileType || resolveFileType(file);

        if (!fileType) {
          throw new Error(
            'File type not specified and could not be determined'
          );
        }

        const receiptImageId = imageId ?? uuid();
        setUploadState?.({
          receiptImageId,
          finished: false,
          error: undefined,
          progress: 0,
        });

        const md5Hash = await computeMd5Hash(file);
        const uploadUrl = await getUploadUrl(
          receiptImageId,
          fileType,
          md5Hash
        ).then((url) => url);

        if (!uploadUrl) {
          setUploadState?.((oldState) => ({
            progress: 0,
            receiptImageId,
            finished: false,
            ...(oldState ?? {}),
            error: new Error('Failed to retrieve upload url'),
          }));
          if (!setUploadState) {
            throw Error('Failed to retrieve upload url');
          }
          return undefined;
        }

        const headers = {
          'content-type': fileType,
          'content-length': file.size.toString(),
          'content-disposition': encodeContentDisposition(file.name),
          'content-md5': md5Hash,
        };

        if (useFetch) {
          await fetch(uploadUrl, {
            method: 'PUT',
            headers: new Headers(headers),
            body: file,
          });
        } else {
          await axios.put(uploadUrl, file, {
            headers,
            onUploadProgress: (data) => {
              const progress = data.loaded / (data.total ?? 1);
              setUploadState?.((oldState) => ({
                receiptImageId,
                finished: false,
                error: undefined,
                ...oldState,
                progress,
              }));
            },
          });
        }

        setUploadState?.((oldState) => ({
          receiptImageId,
          progress: 1,
          ...oldState,
          finished: true,
          error: undefined,
        }));
        return receiptImageId;
      } catch (e) {
        Sentry.captureException(e);
        setUploadState?.((oldState) => ({
          receiptImageId: undefined,
          progress: 1,
          ...oldState,
          finished: false,
          error: e as Error,
        }));
        if (!setUploadState) {
          throw e;
        }
        return undefined;
      }
    },
    [uuid, setUploadState, computeMd5Hash, getUploadUrl]
  );
};

export const useUploadFile = ({
  uuid,
  getUploadUrl,
  computeMd5Hash,
}: UseUploadReceiptImageProps): UseUploadReceiptImageResult => {
  const [uploadState, setUploadState] = useState<UploadState | undefined>(
    undefined
  );

  const upload = useUploadFileBase({
    uuid,
    getUploadUrl,
    setUploadState,
    computeMd5Hash,
  });

  return {
    upload,
    uploadState,
  };
};
